/**
 * `<ArtifactPane>` — persistent right-column panel for artifact content.
 *
 * Used in the new UI for Phase 1 (Discovery questionnaire) and Phase 2
 * (Game Plan review). The pane is always mounted so that child components
 * with IPC subscriptions (e.g. InterviewSlotOverlay) stay active even
 * when the pane is visually closed. CSS width animation reveals/hides it.
 *
 * Layout: sits to the right of the chat column as a flex sibling, NOT
 * as an overlay. When open, the chat column naturally narrows.
 */
import * as React from 'react';
import { X } from 'lucide-react';

interface ArtifactPaneProps {
  isOpen: boolean;
  /** When omitted, the close button is hidden — use this when the pane
   *  must stay open until the user completes an in-progress action
   *  (e.g. an active bulk interview). */
  onClose?: () => void;
  title: string;
  /** Optional action nodes rendered between the title and the close button. */
  headerActions?: React.ReactNode;
  children: React.ReactNode;
}

export function ArtifactPane({
  isOpen,
  onClose,
  title,
  headerActions,
  children,
}: ArtifactPaneProps): React.JSX.Element {
  return (
    /* Clip container — handles width animation; no visible styling */
    <div
      data-testid="artifact-pane"
      aria-hidden={!isOpen}
      style={{
        width: isOpen ? '50%' : '0',
        minWidth: isOpen ? '50%' : '0',
        transition:
          'width var(--motion-med) var(--ease-flora), min-width var(--motion-med) var(--ease-flora)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        padding: isOpen ? '10px' : '0',
      }}
    >
      {/* Floating panel — rounded, bordered, shadowed */}
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '14px',
          border: '1px solid var(--border-alpha-1)',
          background: 'var(--bg-elev-1)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.28)',
        }}
      >
        {/* Header */}
        <div
          className="flex shrink-0 items-center gap-3 px-4 py-3"
          style={{ borderBottom: '1px solid var(--border-alpha-1)' }}
        >
          <span
            className="flex-1 text-[11px] uppercase tracking-[0.12em]"
            style={{ color: 'var(--text-3)', fontWeight: 400 }}
          >
            {title}
          </span>
          {headerActions && <div className="flex shrink-0 items-center gap-2">{headerActions}</div>}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close panel"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors hover:bg-white/[0.08]"
              style={{ color: 'var(--text-3)' }}
            >
              <X size={14} strokeWidth={1.75} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
      </div>
    </div>
  );
}
